import {
  AutocompleteArrayInput,
  BooleanField,
  BulkDeleteButton,
  BulkUpdateButton,
  ColumnsButton,
  CreateButton,
  DataTable,
  ExportButton,
  FilterButton,
  ImageField,
  List,
  NullableBooleanInput,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  SelectArrayInput,
  SelectField,
  SelectInput,
  SimpleForm,
  TextArrayInput,
  TextField,
  TextInput,
  TopToolbar,
  UrlField,
  useCanAccess,
} from 'react-admin';
import { BulkUpdateFormButton } from '@react-admin/ra-form-layout';
import { Check, Clear } from '@mui/icons-material';
import { ReactElement } from 'react';
import { BidResource } from '../../Entities/Bid';
import {
  createFilterQuery,
  DEFAULT_LIST_PAGE_IMAGE_MAX_HEIGHT,
  DEFAULT_LIST_PAGE_IMAGE_MAX_WIDTH,
  DEFAULT_LIST_PAGE_PAGINATION_LIMIT,
} from '../../utils';
import TzDateField from '../../Components/TzDate/TzDateField';
import TzDateTimeInput from '../../Components/TzDate/TzDateTimeInput';
import { APPROVAL_STATUS, APPROVAL_STATUS_CHOICES, BID_TYPE_CHOICES } from './utils';

function filters(
  canListCategories: boolean,
  canListMerchants: boolean,
  canListProducts: boolean,
): ReactElement[] {
  return [
    <TextArrayInput key="id" label="ID" source="equals.id" />,
    <SelectArrayInput
      key="approval_status"
      label="Approval Status"
      source="equals.approval_status"
      choices={APPROVAL_STATUS_CHOICES}
    />,
    <SelectArrayInput
      key="bid_type"
      label="Bid Type"
      source="equals.bid_type"
      choices={BID_TYPE_CHOICES}
    />,
    canListMerchants && (
      <ReferenceArrayInput
        key="merchant"
        label="Merchant"
        source="merchant.id"
        reference="merchants"
      >
        <AutocompleteArrayInput
          label="Merchant"
          optionText="attributes.merchant_name"
          filterToQuery={createFilterQuery(['merchant_name'])}
        />
      </ReferenceArrayInput>
    ),
    canListCategories && (
      <ReferenceArrayInput
        key="category"
        label="Category"
        source="category.id"
        reference="categories"
      >
        <AutocompleteArrayInput
          label="Category"
          optionText="attributes.full_name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    <NullableBooleanInput
      key="is_comparable"
      label="Is In Comparable Category?"
      source="equals.category.is_offer_comparison_allowed"
    />,
    canListProducts && (
      <ReferenceArrayInput key="product" label="Product" source="product.id" reference="products">
        <AutocompleteArrayInput
          label="Product"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    <NullableBooleanInput key="active" label="Active?" source="attributes.active" />,
    <TextArrayInput key="name" label="Name Contains" source="contains.name" />,
    <TextArrayInput key="description" label="Description Contains" source="contains.description" />,
    <TextInput key="url" label="URL Contains" source="contains.url" />,
    <TextInput key="site_id" label="Site ID" source="equals.site_id" />,
    <NumberInput key="bid_cents_gte" label="Min Bid CPC" source="greaterOrEqual.bid_cents" />,
    <NumberInput key="bid_cents_lte" label="Max Bid CPC" source="lessOrEqual.bid_cents" />,
    <TzDateTimeInput key="starts_at_after" label="Starts After" source="greaterThan.starts_at" />,
    <TzDateTimeInput key="starts_at_before" label="Starts Before" source="lessThan.starts_at" />,
    <TzDateTimeInput key="ends_at_after" label="Ends After" source="greaterThan.ends_at" />,
    <TzDateTimeInput key="ends_at_before" label="Ends Before" source="lessThan.ends_at" />,
  ].filter((value) => typeof value !== 'boolean');
}

function BulkActions(): ReactElement {
  return (
    <>
      <BulkUpdateButton
        label="Approve"
        icon={<Check />}
        data={{ attributes: { approval_status: APPROVAL_STATUS.APPROVED } }}
      />
      <BulkUpdateButton
        label="Reject"
        icon={<Clear />}
        data={{ attributes: { approval_status: APPROVAL_STATUS.REJECTED } }}
      />
      <BulkUpdateFormButton mutationMode="pessimistic">
        <SimpleForm>
          <SelectInput
            choices={APPROVAL_STATUS_CHOICES.filter((c) => c.id !== APPROVAL_STATUS.PENDING)}
            label="Approval Status"
            source="attributes.approval_status"
          />
          <TextInput label="Remarks" source="attributes.remarks" multiline />
        </SimpleForm>
      </BulkUpdateFormButton>
      <BulkDeleteButton mutationMode="pessimistic" />
    </>
  );
}

function BidListActions(): ReactElement {
  return (
    <TopToolbar>
      <ColumnsButton />
      <FilterButton />
      <CreateButton />
      <ExportButton />
    </TopToolbar>
  );
}

export default function BidList(): ReactElement {
  const categoryAccess = useCanAccess({ resource: 'categories', action: 'list' });
  const merchantAccess = useCanAccess({ resource: 'merchants', action: 'list' });
  const productAccess = useCanAccess({ resource: 'products', action: 'list' });
  const bidWriteAccess = useCanAccess({ resource: 'bids', action: 'write' });

  const canListCategories = categoryAccess.canAccess ?? false;
  const canListMerchants = merchantAccess.canAccess ?? false;
  const canListProducts = productAccess.canAccess ?? false;
  const canWrite = bidWriteAccess.canAccess ?? false;

  const prefetch: string[] = [];
  if (canListCategories) prefetch.push('category');
  if (canListMerchants) prefetch.push('merchant');
  if (canListProducts) prefetch.push('product');

  return (
    <List<BidResource>
      actions={<BidListActions />}
      filters={filters(canListCategories, canListMerchants, canListProducts)}
      perPage={DEFAULT_LIST_PAGE_PAGINATION_LIMIT}
      queryOptions={{ meta: { prefetch } }}
    >
      <DataTable
        bulkActionButtons={canWrite && <BulkActions />}
        hiddenColumns={[
          'Offer URL',
          'Updated',
          'attributes.description',
          'attributes.offer_price',
          'attributes.retail_price',
          'attributes.site_id',
        ]}
        rowClick={canWrite ? 'edit' : 'show'}
      >
        <DataTable.Col label="ID" source="id" />
        <DataTable.Col label="Image">
          <ImageField
            sortable={false}
            source="attributes.image_url"
            sx={{
              '& img': {
                maxHeight: DEFAULT_LIST_PAGE_IMAGE_MAX_HEIGHT,
                maxWidth: DEFAULT_LIST_PAGE_IMAGE_MAX_WIDTH,
              },
            }}
          />
        </DataTable.Col>
        <DataTable.Col label="Name" source="attributes.name" />
        {canListMerchants && (
          <DataTable.Col label="Merchant">
            <ReferenceField source="relationships.merchant.data.id" reference="merchants">
              <TextField source="attributes.merchant_name" />
            </ReferenceField>
          </DataTable.Col>
        )}
        {canListCategories && (
          <DataTable.Col label="Category">
            <ReferenceField source="relationships.category.data.id" reference="categories">
              <TextField source="attributes.full_name" />
            </ReferenceField>
          </DataTable.Col>
        )}
        {canListProducts && (
          <DataTable.Col label="Product">
            <ReferenceField source="relationships.product.data.id" reference="products">
              <TextField source="attributes.name" />
            </ReferenceField>
          </DataTable.Col>
        )}
        <DataTable.Col label="Description" source="attributes.description" />
        <DataTable.Col label="Offer URL">
          <UrlField source="attributes.url" />
        </DataTable.Col>
        <DataTable.NumberCol label="Bid CPC" source="attributes.bid_cents" />
        <DataTable.NumberCol label="Offer Price" source="attributes.offer_price" />
        <DataTable.NumberCol label="Retail Price" source="attributes.retail_price" />
        <DataTable.Col label="Approval Status">
          <SelectField
            emptyText={APPROVAL_STATUS_CHOICES.find((c) => c.id === APPROVAL_STATUS.PENDING)?.name}
            source="attributes.approval_status"
            choices={APPROVAL_STATUS_CHOICES}
          />
        </DataTable.Col>
        <DataTable.Col label="Active?">
          <BooleanField source="attributes.active" />
        </DataTable.Col>
        <DataTable.Col label="Starts">
          <TzDateField source="attributes.starts_at" />
        </DataTable.Col>
        <DataTable.Col label="Ends">
          <TzDateField source="attributes.ends_at" />
        </DataTable.Col>
        <DataTable.Col label="Updated">
          <TzDateField source="attributes.updated_at" />
        </DataTable.Col>
        <DataTable.Col label="Remarks" source="attributes.remarks" />
        <DataTable.Col label="Bid Type">
          <SelectField source="attributes.bid_type" choices={BID_TYPE_CHOICES} />
        </DataTable.Col>
        <DataTable.Col label="Site ID" source="attributes.site_id" />
      </DataTable>
    </List>
  );
}
